import { CSAdsManager } from '@accedo/vmap-vast/manager';
import { Messenger } from '##/utils/Messenger';
import { ListenerData } from '##/utils/types';
import {
  ClientSideAd,
  BVODResponse,
  LiveDAIAdResponse,
  LiveDAIResponse,
  VODPlaybackResponse,
} from '##/services/tenApi/dai/types';
import {
  LiveChannel,
  LiveV2Response,
} from '##/services/tenApi/collections/types';
import {
  ControlsDetails,
  ID3TagData,
  ProgressBarData,
  ResumeWatchingData,
  PlayerStates,
} from '../PlayerTypes';

class MessengersClass {
  // outgoing messages
  playerState$ = new Messenger<PlayerStates>(PlayerStates.Stopped);
  progressBarData$ = new Messenger<ProgressBarData>({
    endValue: '',
    startValue: '',
    percent: 0,
  });

  fastChannelsLoaded$ = new Messenger<boolean>(false);
  fastChannelsShowing$ = new Messenger<boolean>(false);
  liveProgramTimeAndTargetBuffer$ = new Messenger<{
    programTime: number;
    startBufferTime: number;
  }>({
    programTime: 0,
    startBufferTime: 0,
  });

  controlsVisible$ = new Messenger<boolean>(false);
  subtitlesAvailable$ = new Messenger<boolean>(true);
  focusChanged$ = new Messenger<string>('');
  isLive$ = new Messenger<boolean>(false);
  closedCaptionsToggle$ = new Messenger<boolean>(
    localStorage.getItem('captionToggle') === 'true',
  );

  playerLoading$ = new Messenger<boolean>(false);
  recreateOztamSession$ = new Messenger<void>(undefined);
  duration$ = new Messenger<number>(0);
  videoInfo$ = new Messenger<ControlsDetails | null>(null);
  ffRewindFactor$ = new Messenger<number>(0);
  currentTime$ = new Messenger<number>(0);
  error$ = new Messenger<string>('');
  cueStarts$ = new Messenger<number[]>([]);
  liveData$ = new Messenger<LiveChannel | null>(null);
  liveChannelsData$ = new Messenger<LiveV2Response | null>(null);
  slateShown$ = new Messenger<boolean>(false);

  // internal commands
  requestShowControls$ = new Messenger<boolean>(false);
  requestPlay$ = new Messenger<void>(undefined);
  requestPause$ = new Messenger<void>(undefined);
  requestPlayPause$ = new Messenger<void>(undefined);
  requestSeekTo$ = new Messenger<number>(-1);
  resumeWatchingData$ = new Messenger<ResumeWatchingData | null>(null);
  fragChanged$ = new Messenger<number>(0);
  setBVODParams$ = new Messenger<BVODResponse[]>([]);

  // PlayerStates mapping for userRequestsState$:
  // user presses pause = PlayerStates.Pause,
  // user presses play = PlayerStates.Playing,
  // user presses playpause = either PlayerStates.Playing or PlayerStates.Pause
  // user finalises seek = PlayerStates.Seeked,
  // user seeks to end and finalises = PlayerStates.Ended,
  // user exits video before end = PlayerStates.Stopped
  userRequestsState$ = new Messenger<PlayerStates>(PlayerStates.Playing);

  // ads
  currentAd$ = new Messenger<ClientSideAd | null>(null);
  adNumberDisplayText$ = new Messenger<string>('');
  liveDAIInfo$ = new Messenger<LiveDAIResponse | null>(null);
  fragTagParsed$ = new Messenger<ID3TagData | null>(null);
  liveDAIAds$ = new Messenger<LiveDAIAdResponse | null>(null);
  vodInfo$ = new Messenger<VODPlaybackResponse | null>(null);
  isInHolidayMode$ = new Messenger<boolean>(false);
  seekToAfterAd$ = new Messenger<number>(-1);
  pastAdTime$ = new Messenger<number>(0);
  displayDaiId$ = new Messenger<string>('');
  onMarqueeItemDataFetched$ = new Messenger<void>(undefined);

  csAdsManager$ = new Messenger<CSAdsManager | null>(null);
  mediaElementReference$ = new Messenger<HTMLMediaElement | undefined>(
    undefined,
  );

  contentResumeRequested$ = new Messenger<void>(undefined);
  setLastOztamPlaybackPosition$ = new Messenger<number>(0);

  // analytics only
  pauseAdDisplay$ = new Messenger<string>('');
  ozTamSessionId$ = new Messenger<string>('');

  removeReceivers = (listenerObjects: ListenerData | ListenerData[]): void => {
    if (Array.isArray(listenerObjects)) {
      while (listenerObjects.length) {
        const next = listenerObjects.pop();
        next?.messenger.removeReceiver(next.receiver);
      }
    } else {
      listenerObjects.messenger.removeReceiver(listenerObjects.receiver);
    }
  };
}

export const Messengers = new MessengersClass();
